import { HolderFormProps } from "@/components/HolderForm/HolderFormProps.ts";
import { UserInfoFI } from "@/components/HolderForm/UserInfoFI.ts";
import { CountrySelect } from "@/components/Inputs/CountrySelect/CountrySelect.tsx";
import { FloatingInput } from "@/components/Inputs/FloatingInput/FloatingInput.tsx";
import { PhoneInput } from "@/components/Inputs/PhoneInput/PhoneInput.tsx";
import { Toggle } from "@/components/Inputs/Toggle/Toggle.tsx";
import { isMandarinoCamp } from "@/helpers/customFeatures.ts";
import {
  addressRuleset,
  companyRuleset,
  emailRuleset,
  nameAndSurnameRuleset,
  nameRuleset,
  postalCodeRuleset,
} from "@/helpers/form/FormRules.ts";
import { ParcelObjectType } from "@/helpers/types/types.ts";
import { useAutofillSync } from "@/helpers/useAutofillSync.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { Building2, User } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

export const HolderForm = (props: HolderFormProps) => {
  const { t } = useTranslation();
  const { register, errors, setValue, watch, trigger, setFocus } = props;
  const showCompanyFields = watch(UserInfoFI.IsCompany);
  const noteCharCount = watch(UserInfoFI.Note)?.length;
  const formRef = useRef<HTMLFormElement>(null);

  const { cartAccommodationItems } = useEngineStore(
    useShallow((state) => ({ cartAccommodationItems: state.cartAccommodationItems })),
  );

  const noteMaxLength = useMemo(() => {
    if (!isMandarinoCamp()) return 250;
    const hasCaravan = cartAccommodationItems.some(
      (item) => item.parcelObjectType === ParcelObjectType.Caravan && item.parcelObjectHasMover !== undefined,
    );
    if (!hasCaravan) return 250;

    const moverYesText = t("campSpecific.caravanHasMoverNote") as string;
    const moverNoText = t("campSpecific.caravanNoMoverNote") as string;
    const reservedChars = Math.max(moverYesText.length, moverNoText.length) + 1;
    return 250 - reservedChars;
  }, [cartAccommodationItems, t]);

  // focus first field on mount
  useEffect(() => {
    setFocus(UserInfoFI.FirstName);
  }, [setFocus]);

  // reset fields if checkbox unchecked, focus company name if checked
  useEffect(() => {
    if (showCompanyFields) {
      setTimeout(() => setFocus(UserInfoFI.CompanyName), 0);
    } else {
      setValue(UserInfoFI.CompanyName, "");
      setValue(UserInfoFI.CompanyAddress, "");
      setValue(UserInfoFI.CompanyVAT, "");
      setValue(UserInfoFI.CompanyCountry, "");
    }
  }, [showCompanyFields, setValue, setFocus]);

  useAutofillSync(formRef, setValue);

  return (
    <div className="c-holder-form-wrapper">
      <form
        className="userInfoForm c-be-booking-form"
        ref={formRef}
        autoComplete="on"
        onSubmit={(e) => e.preventDefault()}
      >
        {/* Hidden submit button — helps browsers recognise this as a submittable form for autofill */}
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />

        <div className="c-be-booking-form__header">
          <div className="c-be-booking-form__header-icon">
            <User size={20} />
          </div>
          <div className="c-be-booking-form__header-text">
            <h3 className="c-be-booking-form__title">{t("resHolderInformation")}</h3>
            <p className="c-be-booking-form__description">{t("resHolderDescription")}</p>
          </div>
        </div>

        <Toggle
          label={t(UserInfoFI.IsCompany)}
          registration={register(UserInfoFI.IsCompany)}
          checked={!!showCompanyFields}
        />

        {showCompanyFields && (
          <>
            <div className="c-be-booking-form__header c-be-booking-form__header--small">
              <div className="c-be-booking-form__header-icon">
                <Building2 size={18} />
              </div>
              <div className="c-be-booking-form__header-text">
                <h4 className="c-be-booking-form__title">{t("companyInfo")}</h4>
              </div>
            </div>
            <div className="userInfoFormFieldsWrap c-be-booking-form__fields">
              <FloatingInput
                label={t(UserInfoFI.CompanyName)}
                placeholder=" "
                registration={register(UserInfoFI.CompanyName, companyRuleset)}
                value={watch(UserInfoFI.CompanyName)}
                error={errors[UserInfoFI.CompanyName]}
                autoComplete="organization"
                required
              />
              <FloatingInput
                label={t(UserInfoFI.CompanyAddress)}
                placeholder=" "
                registration={register(UserInfoFI.CompanyAddress, addressRuleset)}
                value={watch(UserInfoFI.CompanyAddress)}
                error={errors[UserInfoFI.CompanyAddress]}
                autoComplete="street-address"
                required
              />
              <FloatingInput
                label={t(UserInfoFI.CompanyVAT)}
                placeholder=" "
                registration={register(UserInfoFI.CompanyVAT, nameRuleset)}
                value={watch(UserInfoFI.CompanyVAT)}
                error={errors[UserInfoFI.CompanyVAT]}
                required
              />
              <CountrySelect
                register={register}
                name={UserInfoFI.CompanyCountry}
                setValue={setValue}
                watch={watch}
                isMandatory
                trigger={trigger}
                error={errors[UserInfoFI.CompanyCountry]}
              />
            </div>
          </>
        )}

        {showCompanyFields && (
          <div className="c-be-booking-form__header c-be-booking-form__header--small">
            <div className="c-be-booking-form__header-icon">
              <User size={18} />
            </div>
            <div className="c-be-booking-form__header-text">
              <h4 className="c-be-booking-form__title">{t("personalInfo")}</h4>
            </div>
          </div>
        )}

        <div className="userInfoFormFieldsWrap c-be-booking-form__fields">
          <FloatingInput
            label={t(UserInfoFI.FirstName)}
            placeholder=" "
            registration={register(UserInfoFI.FirstName, nameAndSurnameRuleset)}
            value={watch(UserInfoFI.FirstName)}
            error={errors[UserInfoFI.FirstName]}
            autoComplete="given-name"
            required
          />
          <FloatingInput
            label={t(UserInfoFI.LastName)}
            placeholder=" "
            registration={register(UserInfoFI.LastName, nameAndSurnameRuleset)}
            value={watch(UserInfoFI.LastName)}
            error={errors[UserInfoFI.LastName]}
            autoComplete="family-name"
            required
          />
          <CountrySelect
            register={register}
            setValue={setValue}
            watch={watch}
            isMandatory
            name={UserInfoFI.Country}
            trigger={trigger}
            error={errors[UserInfoFI.Country]}
          />
          <FloatingInput
            label={t(UserInfoFI.Address)}
            placeholder=" "
            registration={register(UserInfoFI.Address, addressRuleset)}
            value={watch(UserInfoFI.Address)}
            error={errors[UserInfoFI.Address]}
            autoComplete="street-address"
            required
          />
          <FloatingInput
            label={t(UserInfoFI.PostalCode)}
            placeholder=" "
            registration={register(UserInfoFI.PostalCode, postalCodeRuleset)}
            value={watch(UserInfoFI.PostalCode)}
            error={errors[UserInfoFI.PostalCode]}
            maxLength={15}
            autoComplete="postal-code"
            required
          />
          <FloatingInput
            label={t(UserInfoFI.City)}
            placeholder=" "
            registration={register(UserInfoFI.City, nameRuleset)}
            value={watch(UserInfoFI.City)}
            error={errors[UserInfoFI.City]}
            autoComplete="address-level2"
            required
          />
          <FloatingInput
            label={t(UserInfoFI.Email)}
            placeholder=" "
            registration={register(UserInfoFI.Email, emailRuleset(t("form.invalidEmailFormat")))}
            value={watch(UserInfoFI.Email)}
            error={errors[UserInfoFI.Email]}
            type="email"
            autoComplete="email"
            required
          />
          <PhoneInput
            name={UserInfoFI.Phone}
            watchedCountryIso3={watch(UserInfoFI.Country)}
            error={errors[UserInfoFI.Phone]}
            register={register}
            setValue={setValue}
            trigger={trigger}
            watch={watch}
          />
          <div className="c-be-booking-form__field--fullwidth" style={{ position: "relative" }}>
            <FloatingInput
              label={t(UserInfoFI.Note)}
              placeholder="Any special requests..."
              registration={register(UserInfoFI.Note, { maxLength: { value: noteMaxLength, message: "" } })}
              value={watch(UserInfoFI.Note)}
              error={errors[UserInfoFI.Note]}
              multiline
              rows={5}
              maxLength={noteMaxLength}
            />
            <span className="noteCharCount">
              {noteCharCount} / {noteMaxLength}
            </span>
          </div>
        </div>
      </form>
    </div>
  );
};
